import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { NLPLogo } from '../components/nlp/NLPLogo';
import { ArrowRight, Play } from 'lucide-react';
import { useFoundingCohortCountdown } from '../hooks/useFoundingCohortCountdown';

export function FoundingCohortReplayPage() {
  const navigate = useNavigate();
  const { timeLeft, pad } = useFoundingCohortCountdown();

  const goToSalesPage = () => {
    navigate('/founding-cohort');
    window.scrollTo(0, 0);
  };

  return (
    <div className="min-h-screen bg-nlp-bg">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-nlp-bg/95 backdrop-blur-lg shadow-sm">
        <div className="max-w-[1216px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            <button onClick={() => navigate('/home')} className="flex items-center">
              <NLPLogo className="h-12" />
            </button>
            <Button
              onClick={goToSalesPage}
              className="bg-nlp-forest text-white hover:bg-nlp-forest-light rounded-full"
            >
              View Offers
            </Button>
          </div>
        </div>
      </header>

      {/* Countdown Timer Banner */}
      <div className="fixed top-20 left-0 right-0 z-40 bg-nlp-forest text-nlp-bg py-4 px-4 shadow-md">
        <div className="max-w-[1216px] mx-auto flex items-center justify-center gap-4 text-base md:text-lg font-semibold">
          <span className="text-nlp-gold-light">Founding Cohort Offer Ends In:</span>
          <span className="font-mono text-2xl md:text-3xl tabular-nums tracking-wider">
            {pad(timeLeft.hours)}:{pad(timeLeft.minutes)}:{pad(timeLeft.seconds)}
          </span>
        </div>
      </div>

      {/* Hero with Video */}
      <section className="pt-40 pb-20 md:pt-48 md:pb-28 px-4 md:px-8 bg-nlp-bg relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-nlp-olive/5 rounded-full blur-3xl -translate-y-1/4 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-nlp-gold/5 rounded-full blur-3xl translate-y-1/4 -translate-x-1/4" />
        <div className="max-w-[900px] mx-auto relative text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-nlp-cream rounded-full mb-6">
            <Play className="w-4 h-4 text-nlp-gold-dark" />
            <span className="text-sm font-semibold text-nlp-forest">Founding Cohort Offer Replay</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-nlp-forest mb-6 leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
            Watch the Founding Cohort Offer Replay
          </h1>
          <p className="text-lg md:text-xl text-nlp-taupe mb-10 max-w-2xl mx-auto leading-relaxed">
            Today I shared how NLP OS connects the 8Ps into one complete journey — from attracting and understanding clients through to delivering change, demonstrating results and building a more profitable, recurring-income practice.
          </p>

          {/* YouTube Embed */}
          <div className="relative w-full max-w-[800px] mx-auto rounded-2xl overflow-hidden shadow-lg mb-10" style={{ aspectRatio: '16 / 9' }}>
            <iframe
              src="https://www.youtube.com/embed/tgaIA-heqck"
              title="Founding Cohort Offer Replay"
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* What Was Covered */}
      <section className="py-16 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            What I Covered Today
          </h2>
          <div className="space-y-4 text-lg text-nlp-taupe leading-relaxed">
            <p>
              I shared how NLP OS connects the 8Ps into one complete journey — from attracting and understanding clients through to delivering change, demonstrating results and building a more profitable, recurring-income practice.
            </p>
            <p>
              I also explained how you could use NLP OS for:
            </p>
            <ul className="space-y-3 ml-4">
              <li className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-nlp-forest text-white flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">1</div>
                <span className="text-lg text-nlp-forest">Human-led or hybrid one-to-one coaching</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-nlp-forest text-white flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">2</div>
                <span className="text-lg text-nlp-forest">Group coaching with support between sessions</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-nlp-forest text-white flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">3</div>
                <span className="text-lg text-nlp-forest">Ongoing client memberships and recurring income</span>
              </li>
            </ul>
            <p>
              Whether you attended live and have not yet joined, or were unable to be there, you can watch the offer replay above.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 md:px-8 bg-nlp-bg">
        <div className="max-w-[800px] mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Review the Platform & Founding Options
          </h2>
          <p className="text-lg text-nlp-taupe mb-2 leading-relaxed">
            You can review the platform, founding options and everything included on the founding cohort page.
          </p>
          <p className="text-base text-nlp-taupe mb-8 leading-relaxed">
            The founding cohort offer is available for 48 hours, and the lifetime places are strictly limited.
          </p>
          <p className="text-lg text-nlp-forest font-medium mb-8 leading-relaxed">
            If NLP OS feels like the right next step for your coaching, training or certification business, you can select your preferred option on the page.
          </p>
          <Button
            size="lg"
            onClick={goToSalesPage}
            className="gap-2 bg-nlp-forest text-white hover:bg-nlp-forest-light rounded-full"
          >
            View Founding Cohort Offers
            <ArrowRight className="w-5 h-5" />
          </Button>
          <p className="text-sm text-nlp-taupe mt-6">Ml, Patrick</p>
        </div>
      </section>
    </div>
  );
}
